import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { alpha, makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const formatWaiting = date => {
	const minutes = Math.max(1, Math.floor((Date.now() - new Date(date).getTime()) / 60000));
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
	return `${Math.floor(hours / 24)} d`;
};

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		cursor: "unset",
		paddingRight: theme.spacing(12),
	},

	ticketSelected: {
		"&.Mui-selected, &.Mui-selected:hover": {
			backgroundColor: alpha(theme.palette.primary.main, 0.08),
		},
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		flex: 1,
		minWidth: 0,
	},

	unreadCount: {
		alignSelf: "center",
		minWidth: 20,
		marginLeft: theme.spacing(1),
		padding: theme.spacing(0, 0.75),
		borderRadius: theme.borderRadius.pill,
		backgroundColor: green[500],
		color: theme.palette.common.white,
		textAlign: "center",
		...theme.typography.caption,
		fontWeight: 500,
	},

	acceptButton: {
		position: "absolute",
		right: theme.spacing(1.5),
		top: "50%",
		transform: "translateY(-50%)",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
		borderRadius: "0 4px 4px 0",
		backgroundColor: theme.palette.queue.fallback,
	},

	userTag: {
		flex: "none",
		alignSelf: "center",
		marginLeft: theme.spacing(1),
		padding: theme.spacing(0, 0.75),
		borderRadius: theme.borderRadius.pill,
		backgroundColor: theme.palette.action.selected,
		color: theme.palette.text.secondary,
		...theme.typography.caption,
	},

	labelsWrapper: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 2,
	},

	labelTag: {
		padding: theme.spacing(0, 0.75),
		borderRadius: theme.borderRadius.pill,
		color: "#fff",
		fontWeight: 600,
		whiteSpace: "nowrap",
		...theme.typography.caption,
	},

	waitingTag: {
		padding: theme.spacing(0, 0.75),
		borderRadius: theme.borderRadius.pill,
		backgroundColor: "#FFF4E5",
		color: "#8A4B00",
		fontWeight: 600,
		whiteSpace: "nowrap",
		...theme.typography.caption,
	},
}));

const TicketListItem = ({ ticket, showConnection = true }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [, setTick] = useState(0);

	const isWaiting = ticket.status !== "closed" && !ticket.isGroup &&
		ticket.lastMessageFromMe === false && !!ticket.lastMessageAt;

	// Keeps the "waiting since" time current
	useEffect(() => {
		if (!isWaiting) return;
		const interval = setInterval(() => setTick(t => t + 1), 60000);
		return () => clearInterval(interval);
	}, [isWaiting]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				classes={{ selected: classes.ticketSelected }}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl}>
						{ticket.contact.name.charAt(0)}
					</Avatar>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
							>
								{ticket.contact.name}
							</Typography>
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
						</span>
					}
					secondary={
						<>
						<span className={classes.contactNameWrapper}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
							>
								{ticket.lastMessage ? (
									<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
								) : (
									<br />
								)}
							</Typography>

							{showConnection && ticket.whatsappId && (
								<span className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>{ticket.whatsapp?.name}</span>
							)}
							{ticket.unreadMessages > 0 && (
								<span className={classes.unreadCount}>
									{ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
								</span>
							)}
						</span>
						{(ticket.labels?.length > 0 || isWaiting) && (
							<span className={classes.labelsWrapper}>
								{(ticket.labels || []).map(label => (
									<span
										key={label.name}
										className={classes.labelTag}
										style={{ backgroundColor: label.color || "#7E16FF" }}
									>
										{label.name}
									</span>
								))}
								{isWaiting && (
									<span className={classes.waitingTag}>
										{i18n.t("advisors.waiting", { time: formatWaiting(ticket.lastMessageAt) })}
									</span>
								)}
							</span>
						)}
						</>
					}
				/>
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
